import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

//All Loading related components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";

import DatasourceClass from "../../service/dataSource";
import DQMClass from "../../service/dqm";
import CommonLayout from "../Common/Layout";

import ComponentSuspense from "../Common/ComponentSuspense";
import HelpContextIcon from "../Common/HelpPanel/HelpContextIcon";
import { KMKBackButton } from "../Common";
import FindingsChart from "./FindingsChart";
import MultiSelectDropDown from "../Common/MultiSelectDropDown";
import MultiSelect from "../../util/multiselect";
import SummaryTable from "./SummaryTable";
import CategoryChart from "./CategoryChart";
import { navQualityDashboard } from "../kmk/AppData";
import Switch from "../Common/Switch";

const CoverageChart = React.lazy(() => import("./CoverageChart"));

const SummaryDashboard = () => {
  const workspace = useSelector((state) => state.workspace);
  const { Name: workspaceName, IsDataVaultEnabled } = workspace.workspace;

  const location = useLocation();

  const [datasets, setDatasets] = useState([]);

  const [findingsCounts, setFindingsCounts] = useState({
    totalFindings: 0,
    criticalCount: 0,
    warningCount: 0,
  });

  const [categories, setCategories] = useState();
  const [summary, setSummary] = useState([]);

  const [includeClean, setIncludeClean] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(true);

  const [selectedDatasets, setSelectedDatasets] = useState([]);

  const { Workspace_id } = useParams();
  const navigate = useNavigate();

  const prevSelectedDS = useMemo(() => location?.state?.Name, [location?.state?.Name]);

  const coverage = useMemo(() => {
    if (!selectedDatasets.length) return 0;

    const allSelectedDS = datasets.filter((ds) => selectedDatasets.includes(ds.name));
    const averageCoverage = allSelectedDS.reduce((sum, { coverage = 0 }) => sum + coverage, 0) / allSelectedDS.length;

    return averageCoverage ?? 0;
  }, [datasets, selectedDatasets]);

  const navigateToHome = useCallback(
    () =>
      navigate(`/my-workspaces/${Workspace_id}/${navQualityDashboard.link}`, {
        state: { Name: prevSelectedDS ?? "" },
      }),
    [Workspace_id, navigate, prevSelectedDS]
  );

  useEffect(() => {
    if (IsDataVaultEnabled !== 1) return navigateToHome();
  }, [Workspace_id, IsDataVaultEnabled, navigate, navigateToHome]);

  useEffect(() => {
    setLoading(true);

    const getData = async () => {
      const datasource = new DatasourceClass();
      const result = await datasource.getDatasourcesOnWorkspace(Workspace_id);

      if (result.success) {
        const allDS = result.data ?? [];
        setDatasets(allDS);
        setSelectedDatasets(allDS.map(({ name }) => name));
      }

      setLoading(false);
    };
    getData();
  }, [Workspace_id, navigate]);

  const getDatasetsFields = useCallback(async () => {
    if (!selectedDatasets.length) return;

    setLoadingSummary(true);
    const dqmClass = new DQMClass();
    const { success, data } = await dqmClass.getDqmFieldsSummary(Workspace_id, {
      datasource: selectedDatasets,
    });

    if (success) {
      const countsCriticality = data.criticality ?? {};
      setFindingsCounts({
        totalFindings: countsCriticality.totalCount ?? 0,
        criticalCount: countsCriticality.criticalCount ?? 0,
        warningCount: countsCriticality.warningCount ?? 0,
      });

      setCategories(data.categories);

      setSummary(data.summary);
    }

    setLoadingSummary(false);
  }, [Workspace_id, selectedDatasets]);

  useEffect(() => {
    getDatasetsFields();
  }, [getDatasetsFields]);

  const breadcrumbArr = [
    { link: "/my-workspaces", displayName: "Workspaces", active: false },
    {
      link: `/my-workspaces/${Workspace_id}`,
      displayName: workspaceName,
      active: false,
    },
    {
      link: `/my-workspaces/${Workspace_id}/dqm`,
      displayName: "Data Quality Dashboard",
      active: false,
    },
    { link: "#", displayName: "Summary", active: true },
  ];

  return (
    <CommonLayout
      title={
        <>
          Summary
          <HelpContextIcon screen="DQM Summary" />
        </>
      }
      loading={loading}
      breadcrumbArr={breadcrumbArr}
    >
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
          pb: 5,
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            padding: "22px 30px 30px 30px",
          }}
        >
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
              size={12}
            >
              <Box>
                <KMKBackButton handleClick={navigateToHome}></KMKBackButton>

                <MultiSelectDropDown
                  dataid="dqmDatasets"
                  variant="standard"
                  placeHolder={<MultiSelect title="Dataset(s)" dataLength={selectedDatasets.length} />}
                  options={datasets}
                  selected={selectedDatasets}
                  isSelectAll={true}
                  onSelectChange={(selectionList) => setSelectedDatasets(selectionList)}
                  disabled={loading}
                  basedOn="name"
                  labelOn="name"
                  allowSearching
                />
              </Box>

              <FormGroup aria-label="position" row>
                <FormControlLabel
                  value={includeClean}
                  control={<Switch sx={{ "&.MuiSwitch-root": { marginLeft: 0 } }} />}
                  label="Include Clean Findings"
                  labelPlacement="end"
                  sx={{ margin: "0", marginRight: "6px" }}
                  onChange={(e) => setIncludeClean(e.target.checked)}
                  checked={includeClean}
                  disabled={loading || loadingSummary}
                />
              </FormGroup>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <ComponentSuspense>
                <CoverageChart datasource={{ coverage }} showLegend={includeClean} loading={loadingSummary} />
              </ComponentSuspense>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <ComponentSuspense>
                <FindingsChart
                  findingsCounts={findingsCounts}
                  includeClean={includeClean}
                  isDatasetSelected={selectedDatasets.length !== 0}
                  showLegend={includeClean}
                  loading={loadingSummary}
                />
              </ComponentSuspense>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <ComponentSuspense>
                <CategoryChart
                  categories={categories}
                  totalRecords={findingsCounts.totalFindings}
                  includeClean={includeClean}
                  isDatasetSelected={selectedDatasets.length !== 0}
                  showLegend={includeClean}
                  loading={loadingSummary}
                />
              </ComponentSuspense>
            </Grid>
          </Grid>

          <SummaryTable
            Workspace_id={Workspace_id}
            selectedDatasets={selectedDatasets}
            summary={summary}
            loadingSummary={loadingSummary}
          />
        </Box>
      </Box>
    </CommonLayout>
  );
};

export default SummaryDashboard;
